import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Trophy, PenLine, ListChecks, Target, GraduationCap,
  Sparkles, ArrowRight, Clock, Flame, FlaskConical, Calculator,
  Globe, Languages, Dumbbell, Home, BellRing,
} from "lucide-react";
import { api } from "@/lib/api";

const TYPE = {
  tennis: { cls: "bg-emerald-100 text-emerald-700", Icon: Trophy },
  writing: { cls: "bg-amber-100 text-amber-700", Icon: PenLine },
  science: { cls: "bg-sky-100 text-sky-700", Icon: FlaskConical },
  math: { cls: "bg-indigo-100 text-indigo-700", Icon: Calculator },
  humanities: { cls: "bg-orange-100 text-orange-700", Icon: Globe },
  language: { cls: "bg-purple-100 text-purple-700", Icon: Languages },
  phe: { cls: "bg-rose-100 text-rose-700", Icon: Dumbbell },
  homeroom: { cls: "bg-slate-100 text-slate-600", Icon: Home },
};

const fmt = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function Overview({ goTo }) {
  const [tasks, setTasks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [exams, setExams] = useState([]);
  const [today, setToday] = useState([]);

  useEffect(() => {
    api.get("/tasks").then((r) => setTasks(r.data));
    api.get("/goals").then((r) => setGoals(r.data));
    api.get("/exams").then((r) => setExams(r.data));
    api.get("/timetable").then((r) => {
      const name = new Date().toLocaleDateString("en-US", { weekday: "long" });
      setToday(r.data.find((d) => d.day === name)?.items || []);
    });
  }, []);

  const pending = tasks.filter((t) => !t.completed);
  const avgGoal = goals.length ? Math.round(goals.reduce((a, g) => a + g.progress, 0) / goals.length) : 0;
  const nextExam = exams.filter((e) => new Date(e.date) >= new Date(new Date().toDateString()))[0];
  const upcomingExams = exams.filter((e) => new Date(e.date) >= new Date(new Date().toDateString())).length;

  // In-app reminders: what to prep for tomorrow
  const reminders = [];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomName = tomorrow.toLocaleDateString("en-US", { weekday: "long" });
  if (["Tuesday", "Wednesday", "Thursday"].includes(tomName)) {
    reminders.push({ icon: Trophy, cls: "bg-emerald-100 text-emerald-600", text: `Tennis practice tomorrow at 6:30 AM — sleep early and pack your kit.` });
  }
  if (tomName === "Wednesday") {
    reminders.push({ icon: PenLine, cls: "bg-amber-100 text-amber-600", text: `Writing Club tomorrow, 3:15–4:15 PM after school.` });
  }
  if (nextExam) {
    const daysTo = Math.ceil((new Date(nextExam.date) - new Date(new Date().toDateString())) / 86400000);
    if (daysTo >= 0 && daysTo <= 7) {
      reminders.push({ icon: GraduationCap, cls: "bg-indigo-100 text-indigo-600", text: `${nextExam.subject} assessment in ${daysTo} day${daysTo !== 1 ? "s" : ""} — check your study plan in Exam Prep.` });
    }
  }

  const stats = [
    { label: "Pending tasks", value: pending.length, Icon: ListChecks, cls: "bg-rose-500", to: "tasks" },
    { label: "Active goals", value: goals.length, Icon: Target, cls: "bg-purple-500", to: "goals" },
    { label: "Goal progress", value: `${avgGoal}%`, Icon: Flame, cls: "bg-amber-500", to: "goals" },
    { label: "Upcoming exams", value: upcomingExams, Icon: GraduationCap, cls: "bg-indigo-500", to: "schedule" },
  ];

  return (
    <div className="space-y-6">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 to-purple-600 p-7 sm:p-9 text-white">
        <div className="absolute -right-10 -top-10 w-56 h-56 bg-white/10 rounded-full blur-2xl" />
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-200">Neev Academy · IB MYP · Grade 9</p>
        <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-2">Let's crush today, Ayaan 💪</h2>
        <p className="text-indigo-100 mt-2 max-w-lg">You have {pending.length} task{pending.length !== 1 ? "s" : ""} pending{nextExam ? ` and your next exam is ${nextExam.subject}` : ""}. Stay sharp on court and in class.</p>
        <button data-testid="hero-ai-btn" onClick={() => goTo("tutor")} className="mt-5 inline-flex items-center gap-2 bg-white text-indigo-700 font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-indigo-50 transition-colors">
          <Sparkles className="w-4 h-4" /> Ask your AI tutor <ArrowRight className="w-4 h-4" />
        </button>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => {
          const Icon = s.Icon;
          return (
            <motion.button key={s.label} onClick={() => goTo(s.to)} data-testid={`stat-${s.to}`}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="text-left bg-white rounded-2xl border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-md transition-transform">
              <div className={`w-10 h-10 rounded-xl ${s.cls} flex items-center justify-center mb-3`}><Icon className="w-5 h-5 text-white" /></div>
              <p className="text-2xl font-heading font-bold text-slate-900">{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
            </motion.button>
          );
        })}
      </div>

      {/* Reminders */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="reminders-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-xl bg-yellow-100 text-yellow-600 flex items-center justify-center"><BellRing className="w-4.5 h-4.5" /></div>
          <h3 className="font-heading font-semibold text-slate-900">Reminders — what to prep next</h3>
        </div>
        {reminders.length === 0 ? (
          <p className="text-sm text-slate-400">Nothing to prep for tomorrow — enjoy your evening! 🌙</p>
        ) : (
          <div className="space-y-2.5">
            {reminders.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                  className="flex items-center gap-3 rounded-xl border border-slate-100 p-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${r.cls}`}><Icon className="w-4 h-4" /></div>
                  <p className="text-sm text-slate-700">{r.text}</p>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Today schedule */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Clock className="w-5 h-5 text-indigo-500" /> Today's plan</h3>
            <button onClick={() => goTo("timetable")} className="text-sm text-indigo-600 font-medium hover:underline">Full timetable →</button>
          </div>
          {today.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No fixed schedule today — enjoy your free day! 🎉</p>
          ) : (
            <div className="space-y-3">
              {today.filter((it) => it.type !== "break").map((it, idx) => {
                const t = TYPE[it.type] || TYPE.homeroom;
                const Icon = t.Icon;
                return (
                  <div key={idx} className="flex items-center gap-4 p-3 rounded-xl border border-slate-100">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.cls}`}><Icon className="w-5 h-5" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 text-sm">{it.title}</p>
                      <p className="text-xs text-slate-500">{fmt(it.start)} – {fmt(it.end)}{it.teacher ? ` · ${it.teacher}` : ""}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pending tasks preview */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ListChecks className="w-5 h-5 text-rose-500" /> To-do</h3>
            <button onClick={() => goTo("tasks")} className="text-sm text-indigo-600 font-medium hover:underline">All →</button>
          </div>
          {pending.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">All caught up! 🎉</p>
          ) : (
            <div className="space-y-2">
              {pending.slice(0, 5).map((t) => (
                <div key={t.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50">
                  <span className={`w-2 h-2 rounded-full ${t.priority === "high" ? "bg-red-500" : t.priority === "medium" ? "bg-amber-500" : "bg-emerald-500"}`} />
                  <span className="text-sm text-slate-700 truncate flex-1">{t.title}</span>
                  <span className="text-xs text-slate-400">{t.subject}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
